import { create } from "zustand";

import { IHttpError } from "models/interface";
import { RankingProjectService } from "../services/rankingProject.service";
import {
  IRankingProjectCard,
  IRankingType,
  IProjectRecordDateRange,
} from "../models/interface";
import { IRankingTemplateCard } from "../models/interface/rankingSetup.interface";
import { IToastMessages } from "../models/interface";
import { RankingProjectCardMenuKeys } from "../models/enums";

export interface RankingProjectDrawerOptions {
  title: string;
  submitBtnLabel: string;
  key: number;
  data: null | IRankingProjectCard;
  toastMessage: null | IToastMessages;
}

interface RankingProjectListState {
  rankingProjectsLoader: boolean;
  dataFetchError: IHttpError | null;
  rankingProjects: IRankingProjectCard[] | null;
  currentProjectSlug: string | null;
  rankingTypes: IRankingType[] | null;
  rankingTemplateOptions: IRankingTemplateCard[] | null;
  projectRecordDateRange: IProjectRecordDateRange | null;
  rankingProjectDrawerState: boolean;
  rankingProjectDrawerOptions: RankingProjectDrawerOptions;
  formLoader: boolean;
  refreshRankingProjectList: (
    project_slug: string,
    silent?: boolean,
  ) => Promise<void>;
  setRankingProjectRunningState: (
    ranking_project_id: number,
    is_running: boolean,
  ) => void;
  getRankingTypes: (project_slug: string) => Promise<IRankingType[]>;
  getRankingTemplateOptions: (
    project_slug: string,
  ) => Promise<IRankingTemplateCard[]>;
  getProjectRecordDateRange: (
    project_slug: string,
  ) => Promise<IProjectRecordDateRange | null>;
  resetRankingTemplateOptions: () => void;
  resetDrawerState: () => void;
  setRankingProjectDrawerState: (state: boolean) => void;
  setRankingProjectDrawerOptions: (
    key?: number,
    rankingProject?: IRankingProjectCard,
  ) => void;
  setFormLoader: (state: boolean) => void;
}

const useStore = create<RankingProjectListState>((set, get) => ({
  rankingProjectsLoader: true,
  dataFetchError: null,
  rankingProjects: null,
  currentProjectSlug: null,

  refreshRankingProjectList: async (
    project_slug: string,
    silent = false,
  ) => {
    if (get().currentProjectSlug !== project_slug) {
      set(() => ({
        currentProjectSlug: project_slug,
        rankingProjects: null,
        dataFetchError: null,
      }));
    }
    if (!silent) {
      set(() => ({ rankingProjectsLoader: true }));
    }
    try {
      const { data } = await new RankingProjectService().getRankingProjects(
        project_slug,
      );
      set(() => ({
        rankingProjects: data.data.ranking_projects
          ? [...data.data.ranking_projects]
          : [],
        rankingProjectsLoader: false,
        dataFetchError: null,
      }));
    } catch (error: any) {
      if (!silent) {
        set(() => ({ dataFetchError: error, rankingProjectsLoader: false }));
      }
    }
  },

  setRankingProjectRunningState: (ranking_project_id, is_running) => {
    set((state) => ({
      rankingProjects: state.rankingProjects
        ? state.rankingProjects.map((project) =>
            project.id === ranking_project_id
              ? { ...project, is_running }
              : project,
          )
        : state.rankingProjects,
    }));
  },

  rankingTypes: null,
  rankingTemplateOptions: null,
  projectRecordDateRange: null,

  getProjectRecordDateRange: async (project_slug: string) => {
    const cached = get().projectRecordDateRange;
    if (cached) {
      return cached;
    }
    const { data } =
      await new RankingProjectService().getProjectRecordDateRange(project_slug);
    const projectRecordDateRange = data.data.record_date_range ?? null;
    set(() => ({ projectRecordDateRange }));
    return projectRecordDateRange;
  },

  getRankingTypes: async (project_slug: string) => {
    const cached = get().rankingTypes;
    if (cached) {
      return cached;
    }
    const { data } = await new RankingProjectService().getRankingTypes(
      project_slug,
    );
    const rankingTypes = data.data.ranking_types ?? [];
    set(() => ({ rankingTypes }));
    return rankingTypes;
  },

  getRankingTemplateOptions: async (project_slug: string) => {
    const cached = get().rankingTemplateOptions;
    if (cached) {
      return cached;
    }
    const { data } =
      await new RankingProjectService().getRankingTemplatesForProject(
        project_slug,
      );
    const rankingTemplateOptions = data.data.ranking_templates ?? [];
    set(() => ({ rankingTemplateOptions }));
    return rankingTemplateOptions;
  },

  resetRankingTemplateOptions: () => {
    set(() => ({ rankingTemplateOptions: null }));
  },

  rankingProjectDrawerState: false,
  rankingProjectDrawerOptions: {
    title: "Create Ranking Project",
    submitBtnLabel: "Create",
    key: 0,
    data: null,
    toastMessage: null,
  },
  formLoader: false,

  resetDrawerState: () => {
    set(() => ({
      rankingProjectDrawerOptions: {
        title: "Create Ranking Project",
        submitBtnLabel: "Create",
        key: 0,
        data: null,
        toastMessage: null,
      },
    }));
  },
  setRankingProjectDrawerState: (state) => {
    set(() => ({ rankingProjectDrawerState: state }));
  },
  setRankingProjectDrawerOptions: (key, rankingProject) => {
    const titles: any = {
      0: "Create Ranking Project",
    };
    titles[RankingProjectCardMenuKeys.EDIT_RANKING_PROJECT] =
      `Edit ${rankingProject?.title}`;

    const toastMessages: any = {
      0: {
        loading: "Creating ranking project...",
        success: "Ranking project created successfully!",
        error: "Ranking project creation failed. Please try again!",
      },
    };
    toastMessages[RankingProjectCardMenuKeys.EDIT_RANKING_PROJECT] = {
      loading: "Updating ranking project...",
      success: "Ranking project updated successfully!",
      error: "Error in updating ranking project. Please try again!",
    };

    set(() => ({
      rankingProjectDrawerOptions: {
        key: key ?? 0,
        title: titles[key ?? 0],
        submitBtnLabel: key ? "Save Changes" : "Create",
        data: rankingProject ?? null,
        toastMessage: toastMessages[key ?? 0],
      },
    }));
  },
  setFormLoader: (state) => {
    set(() => ({ formLoader: state }));
  },
}));

export const useRankingProjectListStore = useStore;
